<template>
  <div class="extraction-action-controls">
    <div class="labels-and-back-link mr-4">
      <div class="labels">
        <last-voters-cluster-prefix
          v-if="showLastExtractorsPrefix"
          :voters="lastExtractors"
        />
        {{ label }}
      </div>
      <change-previous-stage-link
        v-if="showBackToPreviousStageLink"
        previous-stage="Full Text Review"
        @click="$emit('change-previous-stage')"
      />
    </div>
    <div class="actions">
      <action-button
        v-if="hasButton"
        color="primary"
        :disabled="disabled"
        @click="$emit(buttonEvent, reference)"
      >
        {{ buttonLabel }}
      </action-button>
    </div>
  </div>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import LastVotersClusterPrefix from './LastVotersClusterPrefix';
import ChangePreviousStageLink from './ChangePreviousStageLink';
import { Review, Reference, Researcher } from '@/models';
import { ReferenceActionState } from '@/enums';

const ACTION_STATE_TO_PROPS = {
  [ReferenceActionState.PENDING_ACTION]: {
    label:
      'This reference has been included in the review and is ready for data extraction to commence.',
    buttonLabel: 'Start Extraction',
    buttonEvent: 'extract',
    showBackToPreviousStageLink: true,
  },
  [ReferenceActionState.PENDING_ACTION_BY_YOU]: {
    prefixWithLastExtractor: true,
    label:
      'extracted this reference. To complete the process you may also extract it.',
    buttonLabel: 'Start Extraction',
    buttonEvent: 'extract',
  },
  [ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN]: {
    label:
      'You submitted an extraction for this reference. You may change it if you like.',
    buttonLabel: 'Modify Extraction',
    buttonEvent: 'extract',
  },
  [ReferenceActionState.PENDING_ACTION_BY_OTHERS]: {
    label:
      'Requires an extraction by users with elevated permissions. No further action required from you.',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU]: {
    prefixWithLastExtractors: true,
    label: 'submitted conflicting extractions. Please resolve them.',
    buttonLabel: 'Consensus Review',
    buttonEvent: 'extraction-consensus-review',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS]: {
    prefixWithLastExtractors: true,
    label:
      'submitted conflicting extractions. No further action required from you.',
  },
  [ReferenceActionState.EXTRACTED_BY_CONSENSUS]: {
    prefixWithLastExtractors: true,
    label:
      'extracted this reference after reaching consensus. No further actions required.',
  },
  [ReferenceActionState.EXTRACTED_BY_CONFLICT_RESOLUTION]: {
    prefixWithLastExtractor: true,
    label:
      'extracted this reference after consensus review. No further actions required.',
  },
};

export default {
  name: 'ExtractionActionControls',
  components: {
    ActionButton,
    LastVotersClusterPrefix,
    ChangePreviousStageLink,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'extract',
    'extraction-consensus-review',
    'change-previous-stage',
  ],
  computed: {
    prefixWithLastExtractor() {
      return this.actionProps.prefixWithLastExtractor;
    },
    prefixWithLastExtractors() {
      return this.actionProps.prefixWithLastExtractors;
    },
    showLastExtractorsPrefix() {
      return (
        this.prefixWithLastExtractor || this.prefixWithLastExtractors
      );
    },
    lastExtractors() {
      if (this.prefixWithLastExtractor) {
        return [this.lastExtractor];
      }
      return this.reference.dataExtractionReports
        .slice(0, 2)
        .map((report) => report.reviewer?.researcher)
        .filter(Boolean);
    },
    lastExtractor() {
      const useVotesToFindLast =
        this.actionState ===
        ReferenceActionState.PENDING_ACTION_BY_YOU;
      return useVotesToFindLast
        ? this.reference.dataExtractionReports[0]?.reviewer
            ?.researcher
        : this.reference.dataExtractionOutcome?.reviewer?.researcher;
    },
    label() {
      return this.actionProps.label;
    },
    buttonLabel() {
      return this.actionProps.buttonLabel;
    },
    buttonEvent() {
      return this.actionProps.buttonEvent;
    },
    hasButton() {
      return !!this.buttonLabel;
    },
    showBackToPreviousStageLink() {
      return this.actionProps.showBackToPreviousStageLink;
    },
    actionProps() {
      return ACTION_STATE_TO_PROPS[this.actionState];
    },
    actionState() {
      if (this.reference.isPendingExtraction()) {
        if (this.review.dataExtractionReportsRequired === 1) {
          return this.isTrustedExtractor
            ? ReferenceActionState.PENDING_ACTION
            : ReferenceActionState.PENDING_ACTION_BY_OTHERS;
        }
        return ReferenceActionState.PENDING_ACTION;
      }

      if (this.reference.isPartiallyExtracted()) {
        const hasExtractionsByYou = this.reference.hasExtractionsFrom(
          this.currentResearcher
        );
        const trustedExtractors = this.review.getTrustedDataExtractionReviewers();
        const hasTrustedExtractions = this.reference.hasExtractionsFromAny(
          trustedExtractors
        );

        if (hasExtractionsByYou) {
          return ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN;
        } else if (this.isTrustedExtractor || hasTrustedExtractions) {
          return ReferenceActionState.PENDING_ACTION_BY_YOU;
        } else {
          return ReferenceActionState.PENDING_ACTION_BY_OTHERS;
        }
      }

      if (this.reference.hasExtractionConflicts()) {
        const conflictResolver = this.review.researcherIsDataExtractionConflictResolver(
          this.currentResearcher
        );
        return conflictResolver
          ? ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU
          : ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS;
      }

      if (this.reference.isExtracted()) {
        return this.reference.dataExtractionReports.length > 2
          ? ReferenceActionState.EXTRACTED_BY_CONFLICT_RESOLUTION
          : ReferenceActionState.EXTRACTED_BY_CONSENSUS;
      }

      throw new Error('Unable to determine extraction action state');
    },
    isTrustedExtractor() {
      return this.review.researcherIsTrustedDataExtractionReviewer(
        this.currentResearcher
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.extraction-action-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;

  font-size: 14px;
  font-weight: 300;
}
</style>
